import React, { useState, useEffect } from "react";
import { Box, Flex } from "@radix-ui/themes";
import { AccountStruct } from "../api";

interface AvatarSelectorProps {
  account: AccountStruct;
  onSelectAvatar: (avatarId: number) => void;
}

const AvatarSelector: React.FC<AvatarSelectorProps> = ({
  account,
  onSelectAvatar,
}) => {
  const [avatars, setAvatars] = useState<number[]>([]);
  const [selectedAvatar, setSelectedAvatar] = useState<number | undefined>(
    account.player_sprite_id,
  );

  // Generate avatar IDs from 1 to 30
  useEffect(() => {
    const avatarIds = Array.from({ length: 30 }, (_, i) => i + 1);
    setAvatars(avatarIds);
  }, []);

  // The account's saved avatar may arrive AFTER mount (the early PlayerJoin ->
  // PlayerInfo round-trip fills it in). Reflect it so the player sees the
  // avatar they used last time pre-selected. The user's own click also updates
  // account.player_sprite_id, so this stays in sync with their picks too.
  useEffect(() => {
    if (account.player_sprite_id !== undefined) {
      setSelectedAvatar(account.player_sprite_id);
    }
  }, [account.player_sprite_id]);

  const handleAvatarClick = (avatarId: number) => {
    setSelectedAvatar(avatarId);
    onSelectAvatar(avatarId);
  };

  return (
    <Box>
      <Box style={{ marginBottom: "10px" }}>
        <p style={{ color: "var(--ro-text)" }}>Pick a player avatar:</p>
      </Box>
      <Flex wrap="wrap" gap="2">
        {avatars.map((avatarId) => {
          const paddedId = avatarId.toString().padStart(2, "0");
          return (
            <Box
              key={avatarId}
              style={{
                width: "60px",
                height: "60px",
                margin: "5px",
                padding: "5px",
                cursor: "pointer",
                border:
                  selectedAvatar === avatarId
                    ? "2px solid var(--ro-accent)"
                    : "2px solid transparent",
                borderRadius: "4px",
                backgroundColor:
                  selectedAvatar === avatarId
                    ? "rgba(31, 168, 224, 0.2)"
                    : "transparent",
              }}
              onClick={() => handleAvatarClick(avatarId)}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  overflow: "hidden",
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "32px",
                    overflow: "hidden",
                    position: "relative",
                    transform: "scale(1.5)",
                  }}
                >
                  <img
                    src={`/assets/spritesheets/PC_${paddedId}.png`}
                    alt={`Avatar ${avatarId}`}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "auto",
                      height: "auto",
                      objectFit: "none",
                      objectPosition: "0px 0px",
                      maxWidth: "none",
                      maxHeight: "none",
                    }}
                  />
                </div>
              </div>
            </Box>
          );
        })}
      </Flex>
      {selectedAvatar && (
        <Box style={{ marginTop: "10px" }}>
          <p style={{ color: "var(--ro-text)" }}>Selected Avatar: {selectedAvatar}</p>
        </Box>
      )}
    </Box>
  );
};

export default AvatarSelector;
